/**
 * Pure helpers for Docs-like comment reanchoring (no TipTap imports).
 */

export function normalizeCommentAnchorText(value: string): string {
  return value.replace(/\u00A0/g, " ").replace(/\s+/g, " ").trim();
}

export function anchorTextMatchesOriginal(atAnchor: string, original: string): boolean {
  const a = normalizeCommentAnchorText(atAnchor).toLowerCase();
  const o = normalizeCommentAnchorText(original).toLowerCase();
  if (!o) return true;
  if (!a) return false;
  if (a === o) return true;
  if (a.includes(o) || o.includes(a)) return true;
  return false;
}

/**
 * Find `needle` in `haystack` (case-insensitive, whitespace-normalized) and
 * return character offsets into the original haystack string when possible.
 */
export function findNormalizedSubstringRange(
  haystack: string,
  needle: string
): { start: number; end: number } | null {
  const n = normalizeCommentAnchorText(needle);
  if (!n) return null;
  const hNorm = normalizeCommentAnchorText(haystack);
  const idx = hNorm.toLowerCase().indexOf(n.toLowerCase());
  if (idx < 0) return null;

  // Map normalized index back approximately onto original by walking both.
  let origI = 0;
  let normI = 0;
  const lowerHay = haystack;
  const targetStart = idx;
  const targetEnd = idx + n.length;
  let start = -1;
  let end = -1;

  const pushNormChar = (ch: string) => {
    if (/\s/.test(ch)) {
      if (normI === 0 || hNorm[normI - 1] === " ") return;
      if (normI === targetStart) start = origI;
      normI += 1;
      if (normI === targetEnd) end = origI + 1;
      return;
    }
    if (normI === targetStart) start = origI;
    normI += 1;
    if (normI === targetEnd) end = origI + 1;
  };

  while (origI < lowerHay.length && end < 0) {
    const ch = lowerHay[origI];
    if (ch === "\u00A0" || /\s/.test(ch)) {
      pushNormChar(" ");
      // collapse consecutive whitespace in original
      while (origI + 1 < lowerHay.length && /\s/.test(lowerHay[origI + 1])) {
        origI += 1;
      }
    } else {
      pushNormChar(ch);
    }
    origI += 1;
  }

  if (start < 0 || end <= start) {
    // Fallback: first literal indexOf
    const lit = haystack.toLowerCase().indexOf(needle.toLowerCase());
    if (lit >= 0) return { start: lit, end: lit + needle.length };
    return null;
  }
  return { start, end };
}

export type CommentRange = { from: number; to: number };

/**
 * Pick the best comment range given candidate sources (Docs-like priority).
 * Callers supply mark/stored/search results and a text extractor.
 */
export function pickCommentAnchorRange(opts: {
  originalQuote: string;
  markRange: CommentRange | null;
  storedRange: CommentRange | null;
  blockScopedRange: CommentRange | null;
  globalSearchRange: CommentRange | null;
  textAt: (range: CommentRange) => string;
}): CommentRange | null {
  const original = normalizeCommentAnchorText(opts.originalQuote);
  const consider = (range: CommentRange | null): CommentRange | null => {
    if (!range || !(range.to > range.from)) return null;
    if (!original) return range;
    const at = opts.textAt(range);
    if (anchorTextMatchesOriginal(at, original)) return range;
    return null;
  };

  return (
    consider(opts.markRange) ??
    consider(opts.storedRange) ??
    consider(opts.blockScopedRange) ??
    consider(opts.globalSearchRange) ??
    opts.blockScopedRange ??
    opts.globalSearchRange ??
    null
  );
}
